import * as yup from 'yup';

const CRISPR_MODES = ['Cas9', 'Cas12a'];
const TALEN_ZFN_MODES = ['TALEN', 'ZFN'];

const validationSchema = yup.object().shape({
    name: yup.string().trim().required('Please enter the analysis name.'),
    mode: yup
        .string()
        .oneOf([...CRISPR_MODES, ...TALEN_ZFN_MODES], 'Please select a valid analysis mode.')
        .required('Please select the analysis mode.'),

    // ---- Cas9 / Cas12a 専用フィールド ----
    targetSiteList: yup
        .string()
        .trim()
        .when('mode', {
            is: (mode) => CRISPR_MODES.includes(mode),
            then: (schema) => schema.required('Please enter the Protospacer sequence.'),
            otherwise: (schema) => schema.notRequired(),
        }),
    pam: yup
        .string()
        .trim()
        .when('mode', {
            is: (mode) => CRISPR_MODES.includes(mode),
            then: (schema) => schema.required('Please enter the PAM sequence.'),
            otherwise: (schema) => schema.notRequired(),
        }),
    substitutionFivePrimeMaxSize: yup.number().min(0).max(3).required(),
    substitutionThreePrimeMaxSize: yup.number().min(0).max(3).required(),
    deletionMaxSize: yup.number().min(0).max(100).required(),
    insertionMaxSize: yup.number().min(0).max(10).required(),

    // ---- TALEN / ZFN 専用フィールド ----
    // binding site 長：TALEN 15–20 nt、ZFN 9–21 nt（仕様書 v1.4 §6.5）
    leftBindingSite: yup
        .string()
        .trim()
        .when('mode', ([mode], schema) => {
            if (mode === 'TALEN') {
                return schema
                    .required('Please enter the Left binding site.')
                    .min(15, 'Left TALEN binding site must be 15–20 nt.')
                    .max(20, 'Left TALEN binding site must be 15–20 nt.')
                    .matches(/^[ATGCatgc]+$/, 'Please enter only A/T/G/C characters.');
            }
            if (mode === 'ZFN') {
                return schema
                    .required('Please enter the Left binding site.')
                    .min(9, 'Left ZF array binding site must be 9–21 nt.')
                    .max(21, 'Left ZF array binding site must be 9–21 nt.')
                    .matches(/^[ATGCatgc]+$/, 'Please enter only A/T/G/C characters.');
            }
            return schema.notRequired();
        }),
    rightBindingSite: yup
        .string()
        .trim()
        .when('mode', ([mode], schema) => {
            if (mode === 'TALEN') {
                return schema
                    .required('Please enter the Right binding site.')
                    .min(15, 'Right TALEN binding site must be 15–20 nt.')
                    .max(20, 'Right TALEN binding site must be 15–20 nt.')
                    .matches(/^[ATGCatgc]+$/, 'Please enter only A/T/G/C characters.');
            }
            if (mode === 'ZFN') {
                return schema
                    .required('Please enter the Right binding site.')
                    .min(9, 'Right ZF array binding site must be 9–21 nt.')
                    .max(21, 'Right ZF array binding site must be 9–21 nt.')
                    .matches(/^[ATGCatgc]+$/, 'Please enter only A/T/G/C characters.');
            }
            return schema.notRequired();
        }),
    spacer: yup
        .number()
        .typeError('Spacer length must be a number.')
        .when('mode', {
            is: (mode) => TALEN_ZFN_MODES.includes(mode),
            then: (schema) =>
                schema
                    .min(1, 'Spacer length must be 1 bp or greater.')
                    .max(30, 'Spacer length must be 30 bp or less.')
                    .required('Please set the spacer length.'),
            otherwise: (schema) => schema.notRequired(),
        }),
    deletionFivePrimeMaxSize: yup
        .number()
        .typeError("Maximum 5' deletion size must be a number.")
        .min(0, "Maximum 5' deletion size must be 0 or greater.")
        .max(15, "Maximum 5' deletion size must be 15 or less.")
        .when('mode', {
            is: (mode) => TALEN_ZFN_MODES.includes(mode),
            then: (schema) => schema.required("Please set the maximum 5' deletion size."),
            otherwise: (schema) => schema.notRequired(),
        }),
    deletionThreePrimeMaxSize: yup
        .number()
        .typeError("Maximum 3' deletion size must be a number.")
        .min(0, "Maximum 3' deletion size must be 0 or greater.")
        .max(15, "Maximum 3' deletion size must be 15 or less.")
        .when('mode', {
            is: (mode) => TALEN_ZFN_MODES.includes(mode),
            then: (schema) => schema.required("Please set the maximum 3' deletion size."),
            otherwise: (schema) => schema.notRequired(),
        }),

    // ---- 共通フィールド ----
    enabledLasso: yup.boolean().required(),
    variableSelection: yup.string().required(),

    // ファイルは File オブジェクトが存在するかどうかでバリデーション
    // File オブジェクト自体のバリデーションは utils/ab1FileUtils.js で行う
    abiControlFile: yup
        .mixed()
        .required('WT sample file is required.'),
    abiEditedFile: yup
        .mixed()
        .required('Edited sample file is required.'),
});

export default validationSchema;
export { CRISPR_MODES, TALEN_ZFN_MODES };
